import { selectMutableWidget, updateWidgetDataByKey, updateWidgetPropertyByPath, updateWidgetValue, useAppDispatch, useAppSelector } from '@libs/store';
import {
    ActionActionTypeEnum,
    JsonPathEnum,
    TCommonInputOption,
    THandleChangeInputPhone,
    TInputData,
    TInputMethods,
    TInputPhoneData,
    TItem,
    TUseCheckBoxHook,
    TUseInput,
    TInputConfig,
    TUseInputPhone,
    TWidget,
    TCheckBoxConfig,
} from '@libs/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const isInputValid = (value: string, mandatory?: boolean, regex?: string): boolean => {
    let isValid = true;
    const trimValue = typeof value === 'string' ? value.trim() : value;
    if (mandatory && !regex) {
        isValid = !!trimValue;
    } else if (mandatory && regex) {
        try {
            const valid = new RegExp(regex);
            isValid = valid.test(trimValue);
        } catch {
            console.log('Wrong regexp expression: ', regex);
            isValid = false;
        }
    } else if (mandatory === false && regex && trimValue) {
        try {
            const valid = new RegExp(regex);
            isValid = valid.test(trimValue);
        } catch {
            console.log('Wrong regexp expression: ', regex);
            isValid = true;
        }
    }
    return isValid;
};

export const multipleValidation = (
    value: string,
    mandatory?: TInputData['mandatory'],
    regex?: TInputData['regex'],
    defaultErrorMessage?: string,
    validations?: TInputData['validations'],
): {
    isValid: boolean;
    errorMessage?: string;
    failedValidationInternalId?: string | null;
} => {
    const _validations = validations ? [...validations] : [];
    if (regex || mandatory) {
        _validations.push({ regex: regex, errorMessage: defaultErrorMessage });
    }
    const validationResult: { isValid: boolean; errorMessage?: string; failedValidationInternalId?: string | null } = {
        isValid: true,
        errorMessage: defaultErrorMessage,
    };

    _validations.forEach((v) => {
        if (validationResult.isValid && !isInputValid(value, mandatory, v.regex)) {
            validationResult.isValid = false;
            validationResult.errorMessage = v.errorMessage ?? defaultErrorMessage;
            validationResult.failedValidationInternalId = v.internalId ?? null;
        }
    });

    return validationResult;
};

export const useInputPhone = (widget: TWidget<TInputPhoneData, TCommonInputOption>): TUseInputPhone => {
    const { uid } = widget;
    const data = widget.data ?? {};
    const dispatch = useAppDispatch();
    const getItemByPhoneNumber = (items: TItem[], value?: string | unknown) =>
        items.find((i) => {
            const itemValueLength = (i.value as string)?.length;
            const substring = (value as string)?.substring?.(0, itemValueLength);
            return i.id === value || i.value === substring;
        });
    const getItemByCountryIso = (items: TItem[], selectedCountryIso: string) =>
        items.find((i) => i?.countryIso?.toLowerCase?.() === selectedCountryIso.toLowerCase());

    const refDefaultRegex = useRef(data.regex);

    const countryItemForInit = useMemo(() => {
        if (Array.isArray(data.items) && data.items.length > 0) {
            const result = data.selectedCountryIso ? getItemByCountryIso(data.items, data.selectedCountryIso) : getItemByPhoneNumber(data.items, data.value);
            return result ?? getItemByPhoneNumber(data.items, data.value);
        }
    }, [data.items, data.selectedCountryIso, data.value]);

    const [iso2, setIso2] = useState<string>(countryItemForInit?.countryIso ?? '');

    useEffect(() => {
        if (countryItemForInit?.regex) {
            dispatch(
                updateWidgetDataByKey({
                    value: countryItemForInit.regex ?? refDefaultRegex.current,
                    key: 'regex',
                    uid: uid,
                }),
            );
        }
    }, [countryItemForInit, dispatch, uid]);

    const handleChangeInputPhone: THandleChangeInputPhone = (
        _value: string,
        data: Record<string, string | boolean | undefined>,
        _event?: React.SyntheticEvent<EventTarget>,
        formattedValue?: string,
    ) => {
        const clearFormattedValue = formattedValue?.replace(/\D/g, '') ?? '';
        dispatch(updateWidgetValue({ value: clearFormattedValue, uid: uid }));
        if (data.isChangeCountry) {
            setIso2(data.countryCode as string);
            dispatch(updateWidgetDataByKey({ value: data.regex ?? refDefaultRegex.current, key: 'regex', uid: uid }));
        }
    };
    return {
        iso2,
        handleChangeInputPhone,
    };
};

export const useInput = (widget: TWidget<TInputData, TCommonInputOption>, methods?: TInputMethods, config?: TInputConfig): TUseInput => {
    const { uid, options, internalId, data, systemMetadata } = widget;
    const { mandatory, regex, validations } = data ?? {};
    const { isHidden = false, isDisabled = false, triggerActionsByType } = methods ?? {};
    const { onChange, onBlur, onFocus, onBeforeSelect, onSelect, getValidationValue, parseValue, skipValidation = false } = config ?? {};

    const input = useAppSelector((state) => selectMutableWidget(state, uid));
    const dispatch = useAppDispatch();

    const inputData = input?.data as unknown as TInputData | undefined;
    const defaultErrorMessage = (inputData?.messages?.errorMessage || inputData?.errorMessage) ?? '';
    const rawValue = input?.data?.value;
    const valueStr = rawValue as string;
    const value = valueStr ?? '';
    const validationValue = getValidationValue ? getValidationValue(rawValue) : value;
    const label = widget.data?.messages?.label || widget.data?.label || '';
    const helperText = widget.data?.messages?.helperText || widget.data?.helperText;

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(defaultErrorMessage);
    const [isError, setError] = useState(input?.options?.isError);
    const isFilled = isFocused || !!value;
    const ref = useRef<HTMLInputElement>(null);

    const updateInputValue = useCallback(
        (val: unknown, targetUid?: string) => {
            dispatch(updateWidgetValue({ value: val, uid: targetUid ?? uid }));
        },
        [dispatch, uid],
    );

    useEffect(() => {
        if (options?.onFocus && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [options?.onFocus]);

    useEffect(() => {
        if (internalId && !skipValidation) {
            const validationResult = multipleValidation(validationValue, mandatory, regex, defaultErrorMessage, validations);
            dispatch(
                updateWidgetPropertyByPath({
                    uid: uid,
                    value: validationResult.isValid,
                    path: JsonPathEnum.IS_VALID,
                }),
            );
        }
    }, [defaultErrorMessage, dispatch, internalId, mandatory, validationValue, regex, uid, validations, skipValidation]);

    const handleChange = useCallback(
        (e?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
            const inputValue = e ? e.target.value : (newValue ?? '');
            const nextValue = parseValue ? parseValue(inputValue, widget) : inputValue;
            updateInputValue(nextValue);
            onChange?.(nextValue, e);
        },
        [onChange, parseValue, updateInputValue, widget],
    );

    const handleBlur = useCallback(
        (e?: React.FocusEvent<Element>) => {
            if (!isDisabled) {
                setIsFocused(false);
            }
            onBlur?.(e);
        },
        [onBlur, isDisabled],
    );

    const handleFocus = useCallback(
        (e?: React.FocusEvent<Element>) => {
            if (!isDisabled) {
                setIsFocused(true);
            }
            onFocus?.(e);
        },
        [onFocus, isDisabled],
    );

    const handleClickDropdown = useCallback(
        (item: TItem, dropdownOptions?: { doNotValidate?: boolean }) => {
            onBeforeSelect?.();
            const selectedValue = (item.value as string) ?? item.title;
            const validationResult = multipleValidation(selectedValue, mandatory, regex, defaultErrorMessage, validations);
            setError(dropdownOptions?.doNotValidate ? false : !validationResult.isValid);
            setErrorMessage(validationResult.errorMessage);
            updateInputValue(selectedValue);
            triggerActionsByType?.(ActionActionTypeEnum.onItemSelected, { value: selectedValue })?.then();
            onSelect?.(item, selectedValue);
        },
        [onBeforeSelect, onSelect, defaultErrorMessage, mandatory, regex, triggerActionsByType, updateInputValue, validations],
    );

    return {
        value,
        isFocused,
        isFilled,
        isError,
        isHidden,
        isDisabled,
        errorMessage,
        defaultErrorMessage,
        label,
        helperText,
        ref,
        systemMetadata,
        handleChange,
        handleBlur,
        handleFocus,
        handleClickDropdown,
        setError,
        setErrorMessage,
        updateInputValue,
    };
};

export const useCheckBox = <TD = TInputData, TO = TCommonInputOption>(widget: TWidget<TD, TO>, config?: TCheckBoxConfig): TUseCheckBoxHook => {
    const { value, errorMessage, isError, label, updateInputValue } = useInput(widget as TWidget<TInputData, TCommonInputOption>, undefined, {
        skipValidation: config?.skipValidation,
    });
    const inputValue = value === '' ? (widget?.data as TInputData)?.value : value;
    const isChecked = inputValue === true || inputValue === 'true';

    const handleChange = useCallback(
        (e?: React.ChangeEvent<HTMLInputElement>, newValue?: boolean) => {
            const finalValue = e ? String(e.target.checked) : (newValue ?? false);
            updateInputValue(finalValue);
        },
        [updateInputValue],
    );

    return {
        handleChange,
        isChecked,
        errorMessage,
        isError,
        label,
    };
};
